import React from "react";
import Draggable, { DraggableEventHandler } from "react-draggable";

import ArrowUpIcon from "icons/ArrowUpIcon";
import ArrowDownIcon from "icons/ArrowDownIcon";
import Close2Icon from "icons/Close2Icon";
import { WidgetId, useDisplaySettings } from "./DisplaySettingsProvider";
import classNames from "classnames";

const DisplayComponent = ({
  id,
  style,
  canAdjustHeight,
  children,
}: {
  id: WidgetId;
  style: React.CSSProperties;
  canAdjustHeight?: boolean;
  children: ({
    sizeMultiplier,
    heightMultiplier,
  }: {
    sizeMultiplier: number;
    heightMultiplier: number;
  }) => React.ReactNode;
}) => {
  const { editMode, state, updateComponent } = useDisplaySettings();

  const componentState = state[id];

  const onDragStop: DraggableEventHandler = (event, position) => {
    if (componentState.x !== position.x || componentState.y !== position.y) {
      updateComponent({
        componentId: id,
        newValues: { x: position.x, y: position.y },
      });
    }
  };

  const grow = () => {
    updateComponent({
      componentId: id,
      newValues: { s: (componentState.s ?? 1) + 0.1 },
    });
  };

  const shrink = () => {
    updateComponent({
      componentId: id,
      newValues: { s: (componentState.s ?? 1) - 0.1 },
    });
  };

  const increaseHeight = () => {
    updateComponent({
      componentId: id,
      newValues: { h: (componentState.h ?? 1) + 0.1 },
    });
  };

  const decreaseHeight = () => {
    updateComponent({
      componentId: id,
      newValues: { h: (componentState.h ?? 1) - 0.1 },
    });
  };

  const hide = () => {
    updateComponent({
      componentId: id,
      newValues: { v: false },
    });
  };

  const currentPosition = React.useMemo(() => {
    return { x: componentState.x, y: componentState.y };
  }, [componentState.x, componentState.y]);

  if (!componentState.v) {
    return null;
  }

  return (
    <Draggable
      position={{ x: currentPosition.x ?? 0, y: currentPosition.y ?? 0 }}
      onStop={onDragStop}
      disabled={!editMode}
    >
      <div
        className={classNames("display-component", editMode && "edit-mode")}
        style={
          {
            ...style,
            position: "absolute",
            minHeight: 40,
            "--size-multiplier": componentState.s,
            "--height-multiplier": componentState.h,
          } as React.CSSProperties
        }
      >
        <div className="resizer">
          <button onClick={grow} className="resizer-button">
            +
          </button>
          <button onClick={shrink} className="resizer-button">
            -
          </button>
        </div>
        <div className="right-controls">
          {canAdjustHeight && (
            <div className="height-sizer">
              <div className="resize-button" onClick={decreaseHeight}>
                <ArrowUpIcon />
              </div>
              <div className="resize-button" onClick={increaseHeight}>
                <ArrowDownIcon />
              </div>
            </div>
          )}
          <button
            onClick={hide}
            className="resizer-button hide-component-button"
            aria-label="Hide component"
            title="Hide component"
          >
            <Close2Icon />
          </button>
        </div>
        {children({
          sizeMultiplier: componentState.s ?? 1,
          heightMultiplier: componentState.h ?? 1,
        })}
      </div>
    </Draggable>
  );
};

export default DisplayComponent;
